import type { ScriptLookupType } from "~/types/ui";
import { useState, type ReactNode } from "react";
import { Dialog } from "~/components/common/Dialog";
import { Button } from "~/components/ui";
import { FileCode, Sheet, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CodeMirrorComponent } from "~/components/common/code-mirror";
import { ScriptLookup } from "~/components/scripts/lookup/script-lookup";
import { ScriptExamples } from "~/components/scripts/script-examples";
import { ScriptApi } from "~/api/script-api";
import type { ScriptTestDto } from "~/types/api";
import { toast } from "sonner";
import { ScriptLogs } from "~/components/scripts/script-logs";
import { InformationDialog } from "~/components/common/information-dialog";

export function CodeMirrorFormField({
  value,
  onChange,
  options,
  height = "300px",
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    label: string;
    value: ScriptLookupType[]; // TODO Add other value type to lookup formulas
  }[];
  height?: string | undefined;
}) {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState({
    title: "",
    description: "",
  });
  const [dialogChild, setDialogChild] = useState<ReactNode>();

  function openExamples() {
    setDialogTitle({
      title: t("scripts.examples.title"),
      description: t("scripts.examples.description"),
    });
    setDialogChild(<ScriptExamples />);
    setOpenDialog(true);
  }

  function openLookupTable() {
    setDialogTitle({
      title: t("scripts.lookupTable.title"),
      description: t("scripts.lookupTable.description"),
    });
    setDialogChild(<ScriptLookup options={options} />);
    setOpenDialog(true);
  }

  async function dryRun() {
    const scriptApi = new ScriptApi();
    const result = await scriptApi.testScript({
      content: value,
    } as ScriptTestDto);

    if (!result.ok) {
      toast.error(t("scripts.errors.testFail"));
      console.error(result.error);
      return;
    }

    if (result.data?.length == 0) {
      toast.success(t("scripts.logs.empty"));
      return;
    }
    setDialogTitle({
      title: t("scripts.logs.title"),
      description: t("scripts.logs.description"),
    });
    setDialogChild(<ScriptLogs logs={result.data} />);
    setOpenDialog(true);
  }

  return (
    <div className="h-[100%] w-[100%] overflow-x-hidden">
      <Dialog
        title={dialogTitle.title}
        description={dialogTitle.description}
        width="98ww"
        open={openDialog}
        onOpenChange={setOpenDialog}
        externalTrigger
        children={dialogChild}
      />
      <div className="flex w-full mb-1">
        <div className="mr-2">
          <Button type="button" variant="outline" onClick={openExamples}>
            <FileCode className="h-[1.2rem] w-[1.2rem]" />
            {t("scripts.fields.examples")}
          </Button>
        </div>

        <div className="mr-2">
          <Button
            className="mr-1"
            type="button"
            variant="outline"
            onClick={openLookupTable}
          >
            <Sheet className="h-[1.2rem] w-[1.2rem]" />
            {t("scripts.fields.idLookup")}
          </Button>
          <InformationDialog
            title={t("information.scripts.idLookup.title")}
            description={t("information.scripts.idLookup.description")}
            hoverText={t("information.scripts.idLookup.hoverText")}
          />
        </div>
        <div className="mr-2">
          <Button
            className="mr-1"
            type="button"
            variant="outline"
            onClick={dryRun}
          >
            <Play className="h-[1.2rem] w-[1.2rem]" />
            {t("scripts.fields.dryRun")}
          </Button>
          <InformationDialog
            title={t("information.scripts.dryRun.title")}
            description={t("information.scripts.dryRun.description")}
            hoverText={t("information.scripts.dryRun.hoverText")}
          />
        </div>
      </div>
      <CodeMirrorComponent
        value={value}
        height={height}
        onChange={(val) => onChange(val)}
      />
    </div>
  );
}
